import React from "react";
import AccordionSummary from "@mui/material/AccordionSummary";
import {
  Accordion,
  IconButton,
  Card,
  Typography,
  Tooltip,
  Stack,
} from "@mui/material";
import Div from "@jumbo/shared/Div";
import { useTranslation } from "react-i18next";
import LoginIcon from "@mui/icons-material/Login";
import GradingIcon from "@mui/icons-material/Grading";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useNavigate } from "react-router-dom";
import BugReportIcon from "@mui/icons-material/BugReport";
import EuroIcon from "@mui/icons-material/Euro";
import BuildIcon from "@mui/icons-material/Build";

const CustomerItem = ({
  item,
  disabled,
  onLogin,
  onBuild,
  onPreview,
  superadmin,
  dsok,
  userType,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = item.users[0];
  const isGastrokiosk = item?.dsokType === "gastrokiosk" ? true : false;
  const rowCustomerType = item?.customerType ?? item?.customer_type;
  const isAdmin = userType === 1;
  // Partner customers: Business Partner = 2, Service Partner = 7.
  const isPartnerCustomer = rowCustomerType === 2 || rowCustomerType === 7;
  // Admin (userType 1) always sees the DSOK detail/edit link; for non-admins it stays hidden
  // on partner customers (types 2 and 7).
  const showDsokDetailsLink =
    item.dsok && (isAdmin || !isPartnerCustomer);
  let isStaging = false;
  let billingEnabled = false;
  if (item?.dsokSettings && item?.dsokSettings !== "null") {
    try {
      const dsokSettings =
        typeof item.dsokSettings === "string"
          ? JSON.parse(item.dsokSettings)
          : item.dsokSettings;
      isStaging = dsokSettings?.customer?.beDomain?.includes("staging");
      const billing =
        dsokSettings?.settings?.billing ?? dsokSettings?.billing;
      billingEnabled = billing?.enabled === true;
    } catch (_) {
      isStaging = false;
    }
  }
  let targetUrl = `/customer/${item.url}/dsok`;
  if (item.dsokType === "smartkiosk") {
    targetUrl = `/dsok/customer-detail/${item.url}`;
  }

  return (
    <Card sx={{ mb: 1 }}>
      <Accordion expanded={false} square sx={{ borderRadius: 2 }}>
        <AccordionSummary
          aria-controls="panel1bh-content"
          id="panel1bh-header"
          sx={{
            px: 3,
            flexDirection: "row-reverse",
            "& .MuiAccordionSummary-content": {
              alignItems: "center",
              justifyContent: "space-between",

              "&.Mui-expanded": {
                margin: "12px 0",
              },
            },
            ".MuiAccordionSummary-expandIconWrapper": {
              color: "text.secondary",
              transform: "none",
              height: 28,
              width: 28,
              alignItems: "center",
              justifyContent: "center",
              mr: 1,

              "&.Mui-expanded": {
                transform: "none",
                color: "primary.main",
                borderColor: "primary.main",
              },

              "& svg": {
                fontSize: "1.25rem",
              },
            },
          }}
        >
          <Div
            sx={{
              display: { xs: "none", md: "block" },
              width: "10%",
              flexShrink: 0,
              px: 1,
            }}
          >
            <Typography
              fontSize={"12px"}
              variant={"h6"}
              color={"text.secondary"}
              mb={0.25}
            >
              {t("pages.presentation.id")}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant={"body1"}>{item.id}</Typography>
              {billingEnabled && (
                <Tooltip
                  title={t("pages.customers.billingEnabled", {
                    defaultValue: "Faktúrácia povolená",
                  })}
                  placement="top-start"
                >
                  <EuroIcon
                    sx={{ fontSize: "16px", color: "success.main" }}
                  />
                </Tooltip>
              )}
              {isStaging && (
                <Tooltip
                  title={t("pages.customers.stagingEnvironment")}
                  placement="top-start"
                >
                  <BugReportIcon sx={{ fontSize: "16px", color: "orange" }} />
                </Tooltip>
              )}
            </Stack>
          </Div>
          <Div sx={{ flex: { xs: "0 1 auto", md: 2 }, flexShrink: 0, px: 1 }}>
            <Typography
              fontSize={"12px"}
              variant={"h6"}
              color={"text.secondary"}
              mb={0.25}
              sx={{
                display: { xs: "none", md: "block" },
              }}
            >
              {t("pages.customers.companyName")}
            </Typography>
            <Typography variant={"body1"}>{item.companyName}</Typography>
          </Div>
          <Div
            sx={{
              flex: { xs: "0 2 auto", md: 2 },
              display: { xs: "none", md: "block" },
              flexShrink: 0,
              px: 1,
            }}
          >
            <Typography
              fontSize={"12px"}
              variant={"h6"}
              color={"text.secondary"}
              mb={0.25}
              sx={{
                display: { xs: "none", md: "block" },
              }}
            >
              {t("pages.screens.status")}
            </Typography>
            <Typography variant={"body1"}>
              {item.status === 4
                ? t("pages.customer.active")
                : t("pages.customer.inactive")}
            </Typography>
          </Div>
          <Div
            sx={{
              flex: { xs: "0 2 auto", md: 2 },
              display: { xs: "none", md: "block" },
              flexShrink: 0,
              px: 1,
            }}
          >
            <Typography
              fontSize={"12px"}
              variant={"h6"}
              color={"text.secondary"}
              mb={0.25}
              sx={{
                display: { xs: "none", md: "block" },
              }}
            >
              {t("pages.customers.city")}
            </Typography>
            <Typography variant={"body1"}>{item.city}</Typography>
          </Div>
          <Div
            sx={{
              flex: { xs: "0 2 auto", md: 2 },
              display: { xs: "none", md: "block" },
              flexShrink: 0,
              px: 1,
            }}
          >
            <Typography
              fontSize={"12px"}
              variant={"h6"}
              color={"text.secondary"}
              mb={0.25}
              sx={{
                display: { xs: "none", md: "block" },
              }}
            >
              {t("pages.customers.country")}
            </Typography>
            <Typography variant={"body1"}>{item.country}</Typography>
          </Div>
          {!dsok && (
            <Div
              sx={{
                flex: { xs: "0 2 auto", md: 2 },
                display: { xs: "none", md: "block" },
                flexShrink: 0,
                px: 1,
              }}
            >
              <Typography
                fontSize={"12px"}
                variant={"h6"}
                color={"text.secondary"}
                mb={0.25}
                sx={{
                  display: { xs: "none", md: "block" },
                }}
              >
                {t("pages.customers.maxScreens")}
              </Typography>
              <Typography variant={"body1"}>{item.maxScreens}</Typography>
            </Div>
          )}
          <Div
            sx={{
              flex: { xs: "0 2 auto", md: 1 },
              px: 1,
            }}
          >
            <Typography
              fontSize={"12px"}
              variant={"h6"}
              color={"text.secondary"}
              mb={0.25}
              sx={{
                display: { xs: "none", md: "block" },
              }}
            >
              {t("pages.presentation.actions")}
            </Typography>
            <Div
              sx={{
                display: "flex",
                flexDirection: "row",
                flexWrap: "nowrap",
                gap: 0.5,
                minWidth: 110,
              }}
            >
              {dsok && typeof onPreview === "function" && (
                <IconButton
                  size="small"
                  variant={"contained"}
                  onClick={() => onPreview(item)}
                >
                  <Tooltip title="Preview" placement="top-start">
                    <VisibilityIcon />
                  </Tooltip>
                </IconButton>
              )}
              {showDsokDetailsLink && (
                <IconButton
                  size="small"
                  variant={"contained"}
                  onClick={() => navigate(`${targetUrl}`)}
                >
                  <Tooltip
                    title={t("pages.customers.dsokDetails")}
                    placement="top-start"
                  >
                    <GradingIcon />
                  </Tooltip>
                </IconButton>
              )}

              {user && (
                <IconButton
                  size="small"
                  variant={"contained"}
                  disabled={disabled}
                  onClick={() => onLogin(item)}
                >
                  <Tooltip
                    title={t("buttons.loginAsCustomer")}
                    placement="top-start"
                  >
                    <LoginIcon />
                  </Tooltip>
                </IconButton>
              )}
              {superadmin && isGastrokiosk === true && (
                <IconButton
                  size="small"
                  variant={"contained"}
                  onClick={onBuild}
                >
                  <Tooltip
                    title={t("pages.customers.build")}
                    placement="top-start"
                  >
                    <BuildIcon />
                  </Tooltip>
                </IconButton>
              )}
            </Div>
          </Div>
        </AccordionSummary>
      </Accordion>
    </Card>
  );
};
/* Todo item prop define */
export default CustomerItem;
